import React, { useEffect, useMemo, useRef, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/router"
import { useFormik } from "formik"
import { signIn } from "next-auth/react"
import { useTranslation } from "next-i18next"
import styled from "styled-components"
import { getQueryPersistedHref } from "utils"

import { ISignInServiceResponse } from "types/next-auth"
import { AuthFormLink } from "components/Layouts/auth"
import SocialProviders from "components/SocialProviders"
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  Input,
  Text,
} from "meditopia-ui/components"
import { styledColor } from "meditopia-ui/utils"
import { signUpService } from "services/auth"
import { useAuthStore } from "store/auth-store"

import { getSignupSchema } from "./validation"

const LegalText = styled.span`
  font-size: 12px;
  line-height: 1.4;
  color: ${styledColor("white.72", "gray.600")};
`

const LegalLink = styled.a`
  font-size: 12px;
  line-height: 1.4;
  color: ${styledColor("secondary.main", "primary.main")};
  text-decoration: none;
`

const ErrorText = styled(Text)`
  color: ${styledColor("warning")};
  margin-top: 4px;
`

const SignupForm = () => {
  const { t } = useTranslation("auth")
  const { setAuthenticated } = useAuthStore()
  const router = useRouter()
  const [serviceError, setServiceError] = useState("")
  const nameRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  const isTurkish = router.locale === "tr"

  const validationSchema = useMemo(
    () => getSignupSchema(router.locale || "en"),
    [router.locale]
  )

  const {
    values,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    touched,
    setValues,
    setFieldValue,
  } = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
      acceptTerms: false,
      acceptDataProcessing: false,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      const { name, email, password } = values
      const { success, errors } = await signUpService({
        data: {
          name,
          email,
          password,
        },
        locale: router.locale!,
      })

      if (success) {
        // @ts-ignore
        const response: ISignInServiceResponse = await signIn("credentials", {
          username: email,
          password,
          redirect: false,
        })

        if (response.error) {
          setServiceError(response.error)
          setSubmitting(false)
          return false
        }

        setAuthenticated(true)
        setSubmitting(false)
      } else {
        setServiceError(errors[0].message)
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    const browserNameValue = nameRef.current?.value
    const browserEmailValue = emailRef.current?.value
    const browserPasswordValue = passwordRef.current?.value

    if (browserNameValue || browserEmailValue || browserPasswordValue) {
      setValues({
        name: browserNameValue || "",
        email: browserEmailValue || "",
        password: browserPasswordValue || "",
        acceptTerms: false,
        acceptDataProcessing: false,
      })
    }
  }, [setValues])

  return (
    <form onSubmit={handleSubmit} autoComplete="off" noValidate>
      <Alert error={serviceError} />
      <Box mb="6">
        <Input
          ref={nameRef}
          type="text"
          name="name"
          placeholder={t("fields.name")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.name}
          error={
            errors.name && touched.name ? t(`errors.name.${errors.name}`) : ""
          }
          required
        />
      </Box>

      <Box mb="6">
        <Input
          ref={emailRef}
          type="email"
          name="email"
          placeholder={t("fields.email")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.email}
          error={
            errors.email && touched.email
              ? t(`errors.email.${errors.email}`)
              : ""
          }
          required
        />
      </Box>

      <Box mb="4">
        <Input
          ref={passwordRef}
          type="password"
          name="password"
          placeholder={t("fields.password")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.password}
          error={
            errors.password && touched.password
              ? t(`errors.password.${errors.password}`)
              : ""
          }
          required
        />
      </Box>

      {isTurkish ? (
        <Box mb="6">
          {/* Privacy Notice - No checkbox, just a link */}
          <Box mb="4">
            <LegalText>
              <LegalLink
                href={t("kvkk.privacyNoticeLink")}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("kvkk.privacyNotice")}
              </LegalLink>
              {t("kvkk.privacyNoticeText")}
            </LegalText>
          </Box>

          {/* Terms of Service - Required checkbox */}
          <Box mb="3">
            <Checkbox
              name="acceptTerms"
              checked={values.acceptTerms}
              onChange={(e) => setFieldValue("acceptTerms", e.target.checked)}
              label={
                <LegalText>
                  <LegalLink
                    href={t("kvkk.termsOfServiceLink")}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("kvkk.termsOfService")}
                  </LegalLink>
                  {t("kvkk.termsOfServiceText")}
                </LegalText>
              }
            />
            {errors.acceptTerms && touched.acceptTerms && (
              <ErrorText variant="body-tiny-regular">
                {t(`errors.acceptTerms.${errors.acceptTerms}`)}
              </ErrorText>
            )}
          </Box>

          {/* Data Processing Consent - Required checkbox */}
          <Box mb="3">
            <Checkbox
              name="acceptDataProcessing"
              checked={values.acceptDataProcessing}
              onChange={(e) =>
                setFieldValue("acceptDataProcessing", e.target.checked)
              }
              label={<LegalText>{t("kvkk.dataProcessingConsent")}</LegalText>}
            />
            {errors.acceptDataProcessing && touched.acceptDataProcessing && (
              <ErrorText variant="body-tiny-regular">
                {t(
                  `errors.acceptDataProcessing.${errors.acceptDataProcessing}`
                )}
              </ErrorText>
            )}
          </Box>
        </Box>
      ) : (
        <a href="https://web.meditopia.com/en/terms-and-conditions">
          <Text mb="8" variant="body-tiny-regular" color="white.72" _light={{ color: "gray.600" }}>
            {t("termsAndConds")}
          </Text>
        </a>
      )}

      <Button
        type="submit"
        disabled={isSubmitting}
        loading={isSubmitting}
        colorScheme="secondary"
        _light={{ colorScheme: "primary" }}
        mb="6"
        width="100%"
      >
        {t("mainActions.signup")}
      </Button>

      <Box display="flex" flexDirection="row" alignItems="center" mb="8">
        <Text
          mr="2"
          variant="body-small-regular"
          color="white.100"
          _light={{ color: "gray.600" }}
        >
          {t("alreadyUser.text")}
        </Text>
        <Link
          href={getQueryPersistedHref({
            href: "/signIn/",
            keys: ["callbackUrl", "forcedTheme", "slackUserId", "slackTeamId"],
            query: router.query,
          })}
          passHref
        >
          <a>
            <AuthFormLink>{t("alreadyUser.action")}</AuthFormLink>
          </a>
        </Link>
      </Box>

      <Divider color="white.16" _light={{ color: "gray.0" }}>
        <Text
          variant="heading-medium"
          color="white.100"
          _light={{ color: "gray.600" }}
        >
          {t("or.signup")}
        </Text>
      </Divider>
      <Box mt="6">
        <SocialProviders />
      </Box>

      <Button
        size="small"
        variant="ghost"
        color="white.100"
        width="100%"
        border="1px solid"
        borderColor="white.16"
        backgroundColor="black.08"
        _light={{
          color: "black.80",
          borderColor: "gray.100",
          backgroundColor: "transparent",
        }}
        mt="6"
        onClick={() =>
          (window.location.href = `https://ssogate.meditopia.com/sso/web?lang=${router.locale}`)
        }
      >
        {t("ssoText")}
      </Button>
    </form>
  )
}

export default SignupForm
